/**
 * `usePoll` for shell-level reads: only the leader tab fetches; every
 * other tab receives the result over the tab channel and exposes it
 * through the same `PollController` shape, so call sites don't care
 * which tab they are in.
 *
 * Followers report `isPaused: true` (someone else is doing the work); their
 * `refetch()` asks the leader to fetch now. When either the lock API or
 * `BroadcastChannel` is missing the tab simply polls for itself.
 *
 * Only for global reads (the inbox badge; later the event stream). A
 * page-scoped poll is inherently per-tab — each tab shows a different case.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { isTabChannelAvailable, postTabMessage, subscribeTabMessages } from '@/lib/tabChannel';
import { PollController, PollOptions, usePoll } from './usePoll';
import { useTabLeader } from './useTabLeader';

const noop = () => {};

export function useLeaderPoll<T>(key: string, options: PollOptions<T>): PollController<T> {
  const enabled = options.enabled ?? true;
  // Without a channel a leader could never share, so don't elect one.
  const coordinated = isTabChannelAvailable();
  const isLeader = useTabLeader(enabled && coordinated) || !coordinated;

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const poll = usePoll<T>({
    ...options,
    enabled: enabled && isLeader,
    onData: (data) => {
      optionsRef.current.onData?.(data);
      postTabMessage({ type: 'poll-result', key, value: data, at: Date.now() });
    },
  });

  const [shared, setShared] = useState<T | undefined>(undefined);
  const pollRef = useRef(poll);
  pollRef.current = poll;
  const isLeaderRef = useRef(isLeader);
  isLeaderRef.current = isLeader;

  useEffect(() => {
    if (!enabled || !coordinated) {
      return undefined;
    }
    return subscribeTabMessages((message) => {
      if (message.key !== key) {
        return;
      }
      if (message.type === 'poll-result') {
        setShared(message.value as T);
        return;
      }
      if (!isLeaderRef.current) {
        return;
      }
      if (message.type === 'poll-request') {
        const value = pollRef.current.data;
        if (value !== undefined) {
          postTabMessage({ type: 'poll-result', key, value, at: Date.now() });
        }
      } else if (message.type === 'poll-refetch') {
        void pollRef.current.refetch();
      }
    });
  }, [enabled, coordinated, key]);

  // Joining as a follower: ask the leader for its latest value rather than
  // waiting up to a full interval. Taking the lead: own data wins.
  useEffect(() => {
    if (!enabled || !coordinated) {
      return;
    }
    if (isLeader) {
      setShared(undefined);
    } else {
      postTabMessage({ type: 'poll-request', key });
    }
  }, [enabled, coordinated, isLeader, key]);

  const followerRefetch = useCallback(async () => {
    postTabMessage({ type: 'poll-refetch', key });
  }, [key]);

  const followerSetData = useCallback<PollController<T>['setData']>((updater) => {
    setShared((prev) =>
      typeof updater === 'function'
        ? (updater as (prev: T | undefined) => T | undefined)(prev)
        : updater
    );
  }, []);

  if (isLeader || !enabled) {
    return poll;
  }

  const data = shared ?? poll.data;
  return {
    data,
    error: undefined,
    isLoading: data === undefined,
    isFetching: false,
    isPaused: true,
    isStopped: false,
    refetch: followerRefetch,
    stop: noop,
    setData: followerSetData,
  };
}
